import { useState } from "react";
import { Icon, SectionHeader } from "../../components/public/ui";
import { useAuthModal } from "../../components/public/auth/AuthModalContext";

type Tab = "reading" | "listening" | "writing" | "speaking";

const tabs: { id: Tab; label: string }[] = [
  { id: "reading", label: "Reading Simulation" },
  { id: "listening", label: "Listening Audio" },
  { id: "writing", label: "Writing Assistant" },
  { id: "speaking", label: "Speaking Studio" },
];

const readingOptions = [
  "A. Their annual subscription is temporarily suspended.",
  "B. They will be charged an additional $2.50 for the extra 15 minutes.",
  "C. There is no charge because all rides are entirely unlimited.",
  "D. The ride must be paid at the contactless terminal immediately.",
];

const sampleEmail = `Dear Mr. Harrison,

I am writing to formally report an issue regarding the central heating system in apartment 402, which I moved into last Friday. Over the past three evenings, the ambient temperature has dropped below 14 degrees Celsius, causing considerable discomfort...`;

const ReadingPanel = () => {
  const [selected, setSelected] = useState(1);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4">
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 rounded-md bg-secondary-fixed/50 text-secondary font-label-sm text-label-sm font-bold">
            CELPIP General Part 2
          </span>
          <span className="font-headline-sm text-headline-sm text-on-surface font-bold">
            Reading to Apply Information
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs font-semibold">
          <span className="text-on-surface-variant flex items-center gap-1">
            <Icon name="highlight" className="text-base" /> Highlight Mode
            Enabled
          </span>
          <span className="px-3 py-1 rounded bg-error-container text-on-error-container font-mono">
            09:15 remaining
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-6 bg-surface-container-low p-6 rounded-xl flex flex-col gap-4 text-sm leading-relaxed text-on-surface">
          <div className="flex items-center justify-between">
            <span className="font-label-md text-label-md font-bold text-primary">
              Flyer: City Bike Share Program
            </span>
            <span className="text-xs text-on-surface-variant">
              Scroll to review
            </span>
          </div>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Metro Commute is expanding its low-carbon pedal assist bike network
            across the southern districts. Riders can unlock bicycles utilizing
            the MetroKey app or contactless payment kiosks located at all rapid
            transit hubs.
          </p>
          <p className="font-body-md text-body-md text-on-surface p-2.5 rounded-lg bg-tertiary-fixed/30">
            <span className="font-bold text-tertiary">Important Clause:</span>{" "}
            "Annual pass subscribers receive unlimited 45-minute rides. Trips
            extending beyond 45 minutes incur a $2.50 per-quarter-hour overage
            fee."
          </p>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Helmets are mandatory under regional provincial statutes.
            Complimentary helmet sanitary liners are replenished daily at major
            terminal lockers.
          </p>
        </div>
        <div className="lg:col-span-6 flex flex-col justify-between h-full gap-6">
          <div className="flex flex-col gap-4">
            <span className="font-label-md text-label-md text-primary font-semibold">
              Question 1 of 8
            </span>
            <h4 className="font-headline-sm text-headline-sm text-on-surface font-bold">
              What happens if an annual pass holder completes a single 60-minute
              journey?
            </h4>
            <div className="space-y-3 pt-2">
              {readingOptions.map((option, i) => {
                const isSelected = selected === i;
                return (
                  <button
                    type="button"
                    key={option}
                    onClick={() => setSelected(i)}
                    className={`w-full text-left p-4 rounded-xl transition-all cursor-pointer flex items-center gap-3 ${
                      isSelected
                        ? "bg-primary-fixed/40"
                        : "bg-surface-container hover:bg-surface-container-high"
                    }`}
                  >
                    {isSelected ? (
                      <div className="w-5 h-5 shrink-0 rounded-full bg-primary flex items-center justify-center text-on-primary">
                        <Icon name="check" className="text-xs" />
                      </div>
                    ) : (
                      <div className="w-5 h-5 shrink-0 rounded-full bg-surface-container-highest flex items-center justify-center">
                        <span className="w-2.5 h-2.5 rounded-full bg-transparent" />
                      </div>
                    )}
                    <span
                      className={`font-body-sm text-body-sm text-on-surface ${isSelected ? "font-semibold" : ""}`}
                    >
                      {option}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center justify-between pt-4">
            <span className="font-label-sm text-label-sm text-tertiary flex items-center gap-1 font-bold">
              <Icon name="task_alt" className="text-base" /> Answer Saved
              Automatically
            </span>
            <button
              type="button"
              className="px-5 py-2.5 rounded-xl bg-primary text-on-primary font-label-md text-label-md hover:bg-primary-container transition-all flex items-center gap-1.5"
            >
              <span>Confirm &amp; Proceed</span>
              <Icon name="arrow_forward" className="text-sm" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ListeningPanel = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between pb-4">
      <span className="px-3 py-1 rounded-md bg-primary-fixed/50 text-primary font-label-sm text-label-sm font-bold">
        CELPIP Listening Task 3
      </span>
      <span className="font-label-sm text-label-sm text-on-surface-variant font-medium">
        Audio plays once during actual exam
      </span>
    </div>
    <div className="bg-surface-container-low p-6 rounded-xl flex flex-col items-center justify-center text-center gap-4 py-12">
      <div className="w-16 h-16 rounded-full bg-primary text-on-primary flex items-center justify-center shadow-lg">
        <Icon name="play_arrow" className="text-3xl" />
      </div>
      <div>
        <h4 className="font-headline-sm text-headline-sm text-on-surface font-bold">
          Conversation: Workplace Schedule Conflict
        </h4>
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Duration: 1 minute 42 seconds • Canadian Accent
        </p>
      </div>
      <div className="w-full max-w-md bg-surface-container-highest h-3 rounded-full overflow-hidden">
        <div className="bg-primary h-full w-[45%] rounded-full" />
      </div>
      <span className="text-xs font-mono text-on-surface-variant">
        0:46 / 1:42
      </span>
    </div>
  </div>
);

const WritingPanel = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between pb-4">
      <span className="px-3 py-1 rounded-md bg-tertiary-fixed/50 text-tertiary font-label-sm text-label-sm font-bold">
        CELPIP Writing Task 1
      </span>
      <span className="font-label-sm text-label-sm text-on-surface-variant font-medium">
        Writing an Email (150-200 words)
      </span>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-6 bg-surface-container-low p-5 rounded-xl text-xs space-y-2">
        <span className="font-bold text-on-surface">Prompt:</span>
        <p className="text-on-surface-variant leading-relaxed">
          You recently rented an apartment and discovered the heating unit does
          not function properly. Write an email to the property manager
          explaining the issue, describing how it affects you, and requesting a
          solution.
        </p>
      </div>
      <div className="lg:col-span-6 flex flex-col gap-3">
        <div className="p-3 bg-surface-container rounded-xl text-xs text-on-surface-variant flex items-center justify-between">
          <span>
            Word Count: <strong>164 words</strong>
          </span>
          <span className="text-tertiary font-bold">✓ Target Range Met</span>
        </div>
        <textarea
          className="w-full h-32 p-3 bg-surface-container-low rounded-xl text-xs text-on-surface resize-none focus:outline-none border-0 font-body-sm"
          readOnly
          value={sampleEmail}
        />
      </div>
    </div>
  </div>
);

const SpeakingPanel = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between pb-4">
      <span className="px-3 py-1 rounded-md bg-secondary-fixed/50 text-secondary font-label-sm text-label-sm font-bold">
        CELPIP Speaking Task 5
      </span>
      <span className="font-label-sm text-label-sm text-on-surface-variant font-medium">
        Comparing and Persuading
      </span>
    </div>
    <div className="bg-surface-container-low p-8 rounded-xl flex flex-col items-center justify-center text-center gap-4">
      <div className="w-16 h-16 rounded-full bg-accent-warm text-on-primary flex items-center justify-center animate-pulse">
        <Icon name="mic" className="text-3xl" />
      </div>
      <h4 className="font-headline-sm text-headline-sm text-on-surface font-bold">
        Recording in Progress: 38s remaining
      </h4>
      <p className="font-body-sm text-body-sm text-on-surface-variant max-w-md">
        Speak clearly into your microphone. Our speech engine assesses rhythm,
        pause duration, and vocabulary variation in real-time.
      </p>
    </div>
  </div>
);

const panels: Record<Tab, () => JSX.Element> = {
  reading: ReadingPanel,
  listening: ListeningPanel,
  writing: WritingPanel,
  speaking: SpeakingPanel,
};

export const PracticePreview = () => {
  const [activeTab, setActiveTab] = useState<Tab>("reading");
  const { openAuth } = useAuthModal();
  const Panel = panels[activeTab];

  return (
    <section
      className="w-full py-20 bg-surface scroll-mt-20"
      id="practice-preview"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionHeader
          className="mb-12"
          eyebrow="Interactive Sandbox"
          title="Experience Smarter Test Preparation"
          description="Switch between skill domains to preview the exam experience before you create an account."
        />

        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex flex-wrap justify-center p-1.5 rounded-xl bg-surface-container gap-1 shadow-inner">
            {tabs.map((tab) => (
              <button
                type="button"
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-lg font-label-md text-label-md font-semibold transition-all ${
                  activeTab === tab.id
                    ? "bg-surface-container-lowest text-primary shadow-sm"
                    : "text-on-surface-variant hover:text-on-surface"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="w-full bg-surface-container-lowest rounded-2xl shadow-xl overflow-hidden p-6 lg:p-8">
          <Panel />
        </div>

        <div className="flex flex-col items-center justify-center pt-10 gap-3">
          <button
            type="button"
            onClick={() => openAuth("signup")}
            className="inline-flex items-center gap-2 font-label-lg text-label-lg px-8 py-4 rounded-xl bg-primary text-on-primary shadow-lg shadow-primary/20 hover:bg-primary-container transition-all hover:scale-105"
          >
            <span>Try a Full Practice Test Free</span>
            <Icon name="arrow_forward" className="text-lg" />
          </button>
          <span className="text-xs text-on-surface-variant font-medium">
            No credit card or download necessary. Runs in any browser.
          </span>
        </div>
      </div>
    </section>
  );
};
